import React, { FC, Fragment, useState } from 'react';
import { useTranslate } from 'react-admin';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import cx from 'classnames';

import MuiTableCell from '~/components/TableCell';
import { H2, JsonField, ReferenceField, TextField } from '~/components';
import { Section } from '~/layout';
import { ApprovalCaseDetail } from '~/types/approvalCase';
import Grid from '@material-ui/core/Grid';
import longdash from '~/utils/Longdash';
import { buildDiffMap, DiffType, flattenRecord, Leaf } from '~/utils/diff';

interface DiffRow {
  key: string;
  currentValue: Leaf | undefined;
  newValue: Leaf | undefined;
  diffType: DiffType;
}

const buildDiff = (current: Record<string, any>, next: Record<string, any>): DiffRow[] => {
  const flatCurrent = flattenRecord(current);
  const flatNext = flattenRecord(next);
  const map = buildDiffMap(current, next);

  return Object.keys(map)
    .sort()
    .map((key) => ({
      key,
      currentValue: flatCurrent[key],
      newValue: flatNext[key],
      diffType: map[key],
    }));
};

const displayValue = (v: Leaf | undefined) => (v == null || v === '' ? longdash : String(v));

const breakableKey = (key: string) => {
  const separator = '.';
  const parts = key.split(separator);

  return parts.map((part, index) => (
    <Fragment key={index}>
      {part}
      {index < parts.length - 1 && (
        <>
          {separator}
          <wbr />
        </>
      )}
    </Fragment>
  ));
};

const DataTab: FC<any> = (props) => {
  const { record } = props;
  const translate = useTranslate();
  const t = (key: string) => translate(`resources.approvals.show.data.${key}`);
  const classes = useStyles();
  const [onlyChanges, setOnlyChanges] = useState(false);

  const approvalCase = record as ApprovalCaseDetail | undefined;
  const isUpdate = approvalCase?.actionType === 'UPDATE' && !!approvalCase?.objectId;

  if (!approvalCase) return null;

  if (isUpdate) {
    const rows: DiffRow[] = buildDiff(approvalCase.originalBody ?? {}, approvalCase.actionBody);

    const hasAnyData = rows.length > 0;
    const visibleRows = onlyChanges ? rows.filter((r) => r.diffType !== 'unchanged') : rows;

    return (
      <div>
        <H2 topSpacing={0} bottomSpacing={2}>
          {t('objectDetails')}
        </H2>
        <Section>
          <Grid container spacing={2} className={classes.bottomSpacing}>
            <Grid item xs={4}>
              <ReferenceField
                record={{ id: approvalCase.objectId, type: approvalCase.objectType }}
              />
            </Grid>
          </Grid>
        </Section>

        <Box className={classes.header}>
          <H2 topSpacing={1} bottomSpacing={2}>
            {t('objectData')}
          </H2>
          <Button size='small' color='primary' onClick={() => setOnlyChanges((v) => !v)}>
            {onlyChanges ? t('showAll') : t('showOnlyChanges')}
          </Button>
        </Box>
        {hasAnyData ? <DiffDataTable rows={visibleRows} /> : <Section>{t('noData')}</Section>}
      </div>
    );
  }

  if (approvalCase?.actionType === 'CREATE') {
    const entries = Object.entries(flattenRecord(approvalCase.actionBody));

    return (
      <div>
        <H2 topSpacing={0} bottomSpacing={2}>
          {t('objectData')}
        </H2>
        {entries.length === 0 ? (
          <Section>{t('noData')}</Section>
        ) : (
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <MuiTableCell className={classes.fieldCol}>{t('field')}</MuiTableCell>
                <MuiTableCell>{t('newValue')}</MuiTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map(([key, value]) => {
                return (
                  <TableRow key={key}>
                    <MuiTableCell className={cx(classes.fieldCol)}>
                      {breakableKey(key)}
                    </MuiTableCell>
                    <MuiTableCell>
                      <TextField textWrap>{displayValue(value)}</TextField>
                    </MuiTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    );
  }

  if (approvalCase?.actionType === 'ATOMIC') {
    const entries = Object.entries(flattenRecord(approvalCase.actionBody));

    return (
      <div>
        <H2 topSpacing={0} bottomSpacing={2}>
          {t('objectDetails')}
        </H2>
        <Section>
          <Grid container spacing={2} className={classes.bottomSpacing}>
            <Grid item xs={4}>
              <ReferenceField
                record={{ id: approvalCase.objectId, type: approvalCase.objectType }}
              />
            </Grid>
          </Grid>
        </Section>

        <H2 topSpacing={1} bottomSpacing={2}>
          {t('actionDetails')}
        </H2>
        {entries.length === 0 ? (
          <Section>{t('noData')}</Section>
        ) : (
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <MuiTableCell className={classes.fieldCol}>{t('parameter')}</MuiTableCell>
                <MuiTableCell>{t('value')}</MuiTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map(([key, value]) => {
                return (
                  <TableRow key={key}>
                    <MuiTableCell className={cx(classes.fieldCol)}>
                      {breakableKey(key)}
                    </MuiTableCell>
                    <MuiTableCell>
                      <TextField textWrap>{displayValue(value)}</TextField>
                    </MuiTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    );
  }

  return (
    <JsonField
      json={approvalCase.actionBody}
      reactJsonOptions={{
        name: null,
        collapsed: false,
        enableClipboard: false,
        displayDataTypes: false,
        style: { wordBreak: 'break-all' },
      }}
    />
  );
};

interface DiffDataTableRows {
  isLoading?: boolean;
  rows: DiffRow[];
}

const DiffDataTable: FC<DiffDataTableRows> = (props) => {
  const { rows, isLoading } = props;
  const classes = useStyles();
  const translate = useTranslate();
  const t = (key: string) => translate(`resources.approvals.show.data.${key}`);

  return (
    <Table className={classes.table}>
      <TableHead>
        <TableRow>
          <MuiTableCell className={classes.fieldCol}>{t('field')}</MuiTableCell>
          <MuiTableCell>{t('currentValue')}</MuiTableCell>
          <MuiTableCell>{t('newValue')}</MuiTableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {isLoading
          ? Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                <MuiTableCell loading />
                <MuiTableCell loading />
                <MuiTableCell loading />
              </TableRow>
            ))
          : rows.map((row) => {
              const colorClass = classes[row.diffType];
              return (
                <TableRow key={row.key}>
                  <MuiTableCell className={cx(classes.fieldCol, colorClass)}>
                    {breakableKey(row.key)}
                  </MuiTableCell>
                  <MuiTableCell className={colorClass}>
                    <TextField textWrap>{displayValue(row.currentValue)}</TextField>
                  </MuiTableCell>
                  <MuiTableCell className={colorClass}>
                    <TextField textWrap>{displayValue(row.newValue)}</TextField>
                  </MuiTableCell>
                </TableRow>
              );
            })}
      </TableBody>
    </Table>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  table: {
    tableLayout: 'fixed',
    width: '100%',
  },
  fieldCol: {
    width: '30%',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
  bottomSpacing: {
    paddingBottom: theme.spacing(3),
  },
  unchanged: {},
  changed: { color: theme.palette.warning.main },
  added: { color: theme.palette.success.main },
  removed: { color: theme.palette.error.main },
}));

export default DataTab;
